import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency, getMonthName } from '../lib/payrollEngine';
import type { Payroll, Employee } from '../lib/supabase';

interface DashboardStats {
  totalEmployees: number;
  activeEmployees: number;
  totalPayrollCost: number;
  totalAbsences: number;
  totalLateMinutes: number;
  pendingPayrolls: number;
  latestPayroll: Payroll | null;
  recentActivity: any[];
}

function StatCard({ icon, label, value, sub, color = 'text-primary', bgColor = 'bg-primary/10' }: any) {
  return (
    <div className="card p-6 relative overflow-hidden group">
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <span className={`text-[11px] font-bold text-slate-400 uppercase tracking-widest`}>{label}</span>
          <span className={`material-symbols-outlined ${color}`}>{icon}</span>
        </div>
        <h3 className="text-4xl font-black text-slate-900 tracking-tighter">{value}</h3>
        {sub && <div className="mt-3 text-xs text-on-surface-variant">{sub}</div>}
      </div>
      <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:scale-110 transition-transform duration-500">
        <span className={`material-symbols-outlined text-9xl`}>{icon}</span>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { company } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalEmployees: 0, activeEmployees: 0, totalPayrollCost: 0,
    totalAbsences: 0, totalLateMinutes: 0, pendingPayrolls: 0,
    latestPayroll: null, recentActivity: [],
  });
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  useEffect(() => {
    if (company) loadStats();
  }, [company]);

  async function loadStats() {
    try {
      // Employees
      const { data: employees, count: totalCount } = await supabase
        .from('employees')
        .select('*', { count: 'exact' })
        .eq('company_id', company!.id);

      const activeCount = employees?.filter(e => e.active).length || 0;

      // Payrolls
      const { data: payrolls } = await supabase
        .from('payrolls')
        .select('*')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
        .limit(5);

      const pendingCount = payrolls?.filter(p => p.status === 'draft' || p.status === 'processing').length || 0;
      const latestPayroll = payrolls?.[0] || null;
      const totalCost = payrolls?.find(p => p.period_year === currentYear && p.period_month === currentMonth)?.total_net || 0;

      // This month attendance
      const startOfMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
      const { data: attendance } = await supabase
        .from('attendance')
        .select('status, late_minutes')
        .eq('company_id', company!.id)
        .gte('date', startOfMonth);

      const absences = attendance?.filter(a => a.status === 'absent').length || 0;
      const lateMinutes = attendance?.reduce((s, a) => s + (a.late_minutes || 0), 0) || 0;

      // Recent payroll items
      const { data: recentItems } = await supabase
        .from('payroll_items')
        .select('*, employee:employees(first_name, last_name, position, department)')
        .eq('company_id', company!.id)
        .order('created_at', { ascending: false })
        .limit(5);

      setStats({
        totalEmployees: totalCount || 0,
        activeEmployees: activeCount,
        totalPayrollCost: totalCost,
        totalAbsences: absences,
        totalLateMinutes: lateMinutes,
        pendingPayrolls: pendingCount,
        latestPayroll,
        recentActivity: recentItems || [],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const regimeLabel = company?.regime === 'general' ? 'Régimen General' :
    company?.regime === 'pequeña_empresa' ? 'REMYPE Pequeña Empresa' : 'REMYPE Microempresa';

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">
            Dashboard
          </h1>
          <p className="text-on-surface-variant mt-1">
            {getMonthName(currentMonth)} {currentYear} · {regimeLabel}
          </p>
        </div>
        <div className="flex gap-3">
          <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-100 text-xs font-bold">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Sistema activo
          </span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon="group"
          label="Total Empleados"
          value={stats.totalEmployees}
          sub={<span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{stats.activeEmployees} activos</span>}
        />
        <StatCard
          icon="account_balance_wallet"
          label={`Planilla ${getMonthName(currentMonth)}`}
          value={stats.totalPayrollCost > 0 ? formatCurrency(stats.totalPayrollCost) : '—'}
          sub={stats.totalPayrollCost === 0 ? <span className="text-amber-600">Sin planilla procesada</span> : <span className="text-xs text-on-surface-variant">Neto a pagar</span>}
        />
        <StatCard
          icon="event_busy"
          label="Faltas este mes"
          value={stats.totalAbsences}
          sub={<span className="text-xs text-on-surface-variant">{stats.totalLateMinutes} minutos de tardanza acumulados</span>}
          color="text-error"
        />
      </div>

      {/* Regime info banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 flex items-start gap-4">
        <span className="material-symbols-outlined text-primary text-2xl mt-0.5">info</span>
        <div>
          <h3 className="font-bold text-on-surface text-sm">Configuración activa: {regimeLabel}</h3>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-on-surface-variant">
            <span>
              <strong className="text-on-surface">Vacaciones:</strong>{' '}
              {company?.regime === 'general' ? '30 días/año' :
               company?.regime === 'pequeña_empresa' ? '15 días/año' : 'No obligatorio'}
            </span>
            <span>
              <strong className="text-on-surface">Gratificaciones:</strong>{' '}
              {company?.regime === 'general' ? '1 sueldo (Jul/Dic)' :
               company?.regime === 'pequeña_empresa' ? '½ sueldo (Jul/Dic)' : 'No aplica'}
            </span>
            <span>
              <strong className="text-on-surface">EsSalud:</strong> {company?.essalud_rate}%
            </span>
            <span>
              <strong className="text-on-surface">CTS:</strong>{' '}
              {company?.regime === 'general' ? 'Completa' :
               company?.regime === 'pequeña_empresa' ? '50%' : 'No aplica'}
            </span>
            <span>
              <strong className="text-on-surface">RMV:</strong> {formatCurrency(company?.rmv || 1025)}
            </span>
          </div>
        </div>
      </div>

      {/* Recent activity table */}
      <div className="card overflow-hidden">
        <div className="p-6 flex items-center justify-between border-b border-outline-variant/10">
          <h3 className="text-lg font-bold text-on-surface font-headline">Actividad Reciente</h3>
          {stats.pendingPayrolls > 0 && (
            <span className="bg-amber-50 text-amber-700 text-xs font-bold px-3 py-1 rounded-full">
              {stats.pendingPayrolls} planilla(s) pendiente(s)
            </span>
          )}
        </div>

        {stats.recentActivity.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">receipt_long</span>
            <p className="text-on-surface-variant text-sm mt-2">No hay actividad reciente</p>
            <p className="text-xs text-outline-variant mt-1">Procesa tu primera planilla para ver datos aquí</p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Empleado', 'Puesto', 'Salario Bruto', 'Neto', 'Estado'].map(h => (
                  <th key={h} className="px-6 py-4 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {stats.recentActivity.map(item => (
                <tr key={item.id} className="hover:bg-surface-container-low/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                        {item.employee?.first_name?.[0]}{item.employee?.last_name?.[0]}
                      </div>
                      <span className="text-sm font-bold text-on-surface">
                        {item.employee?.first_name} {item.employee?.last_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-on-surface-variant">{item.employee?.position}</td>
                  <td className="px-6 py-4 text-sm font-semibold tabular-nums">{formatCurrency(item.gross_salary)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-primary tabular-nums">{formatCurrency(item.net_salary)}</td>
                  <td className="px-6 py-4">
                    <span className="badge-neutral">Procesado</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
